import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import PreviousLandlordModal from '../../../Components/PreviousLandlordModal/PreviousLandlordModal';
import { getApi, postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import {
  MAX_DOCUMENT_FILES_PER_CATEGORY,
  TENANT_DOC_ACCEPT_ATTR,
  MULTI_FILE_HINT_TEXT,
  ID_VERIFICATION_TYPE_OPTIONS,
  isValidTenantDocFile,
} from '../../../constants/tenantDocuments';
import { tenantDocumentsFromApiData, normalizeDocumentUrl, normalizeDocumentUrls } from '../../../Utils/tenantDocuments';

const Documents = ({ onUploadSuccess, requireMinimumDocuments = false, embeddedInModal = false }) => {
  const [selectedIdDocumentType, setSelectedIdDocumentType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [landlordModalViewOnly, setLandlordModalViewOnly] = useState(false);
  const [landlordModalInitialData, setLandlordModalInitialData] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get initial document info from localStorage userData (if available)
  const getInitialDocuments = () => {
    const toUrl = (val) => {
      if (Array.isArray(val) && val.length) return normalizeDocumentUrl(val[0]);
      return normalizeDocumentUrl(val);
    };
    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const userDetails = userData?.user_details || {};
        const tenant = Array.isArray(userDetails.tenant) ? userDetails.tenant[0] || {} : {};

        const payStubUrl = toUrl(
          tenant.pay_stub_url ||
          tenant.payStubUrl ||
          tenant.pay_stub ||
          tenant.payStub ||
          tenant.pay_stubs_url
        );
        const bankStatementUrl = toUrl(
          tenant.bank_statement_url ||
          tenant.bankStatementUrl ||
          tenant.bank_statement ||
          tenant.bankStatement
        );
        const landlordReferenceUrl = toUrl(
          tenant.previous_landlord_doc_url ||
          tenant.previousLandlordDocUrl ||
          tenant.previous_landlord_document ||
          tenant.previousLandlordDocument
        );

        const payStubUrls = Array.isArray(tenant.pay_stub_url) ? tenant.pay_stub_url : (payStubUrl ? [payStubUrl] : []);
        const bankStatementUrls = Array.isArray(tenant.bank_statement) ? tenant.bank_statement : (bankStatementUrl ? [bankStatementUrl] : []);
        return {
          payStubs: {
            file: null,
            fileName: payStubUrl ? 'ID verification' : 'ID verification',
            fileUrl: payStubUrl || null,
            fileUrls: payStubUrls,
            newFront: null,
            newBack: null,
          },
          bankStatements: {
            file: null,
            fileName: bankStatementUrl ? 'Bank statement pdf' : 'Bank statement pdf',
            fileUrl: bankStatementUrl || null,
            fileUrls: bankStatementUrls,
            newFiles: [],
          },
          landlordReference: {
            file: null,
            fileName: landlordReferenceUrl ? 'Previous landlord reference' : 'XYZ pdf',
            fileUrl: landlordReferenceUrl || null,
            landlordData: null,
          },
        };
      }
    } catch (error) {
      console.error('Error reading document info from localStorage:', error);
    }

    return {
      payStubs: { file: null, fileName: 'ID verification', fileUrl: null, fileUrls: [], newFront: null, newBack: null },
      bankStatements: { file: null, fileName: 'Bank statement pdf', fileUrl: null, fileUrls: [], newFiles: [] },
      landlordReference: { file: null, fileName: 'XYZ pdf', fileUrl: null, landlordData: null },
    };
  };

  const [documents, setDocuments] = useState(getInitialDocuments);

  // Fetch tenant documents when Document section is opened
  useEffect(() => {
    const fetchTenantDocuments = async () => {
      try {
        const response = await getApi('/auth/get-tenant-documents');
        const data = response?.data;
        if (!data) return;
        // API returns: pay_stub_url (array), bank_statement (array), previous_landlord_* fields
        const payStubUrls = normalizeDocumentUrls(data.pay_stub_url);
        const bankStatementUrls = normalizeDocumentUrls(data.bank_statement);
        const payStubUrl = payStubUrls[0] || '';
        const bankStatementUrl = bankStatementUrls[0] || '';
        const hasLandlordReference = data.previous_landlord_name || data.previous_landlord_phone || data.previous_landlord_email;
        const landlordData = hasLandlordReference
          ? {
              name: data.previous_landlord_name || '',
              phoneNumber: data.previous_landlord_phone || '',
              email: data.previous_landlord_email || '',
            }
          : null;
        setDocuments({
          payStubs: {
            file: null,
            fileName: payStubUrl ? 'ID verification' : 'ID verification',
            fileUrl: payStubUrl || null,
            fileUrls: payStubUrls,
            newFront: null,
            newBack: null,
          },
          bankStatements: {
            file: null,
            fileName: bankStatementUrl ? 'Bank statement pdf' : 'Bank statement pdf',
            fileUrl: bankStatementUrl || null,
            fileUrls: bankStatementUrls,
            newFiles: [],
          },
          landlordReference: {
            file: null,
            fileName: hasLandlordReference ? 'Previous landlord reference' : 'XYZ pdf',
            fileUrl: null,
            landlordData,
          },
        });
      } catch (error) {
        console.error('Error fetching tenant documents:', error);
      }
    };
    fetchTenantDocuments();
  }, []);

  const handleMultiFileUpload = (type, e) => {
    if (type === 'payStubs') return;
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;

    const valid = chosen.filter((f) => isValidTenantDocFile(f));
    const invalidType = chosen.find((f) => !isValidTenantDocFile(f));
    if (invalidType) {
      toast.error('Only PDF and image files (JPEG, PNG, WebP, GIF) are allowed.');
      return;
    }

    setDocuments((prev) => {
      const current = prev[type]?.newFiles || [];
      const merged = [...current, ...valid].slice(0, MAX_DOCUMENT_FILES_PER_CATEGORY);
      return {
        ...prev,
        [type]: { ...prev[type], newFiles: merged, file: null, fileUrl: null },
      };
    });
    e.target.value = '';
  };

  const pickIdSide = (side) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = TENANT_DOC_ACCEPT_ATTR;
    input.multiple = false;
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!isValidTenantDocFile(file)) {
        toast.error('Only PDF and image files (JPEG, PNG, WebP, GIF) are allowed.');
        return;
      }
      const key = side === 'front' ? 'newFront' : 'newBack';
      setDocuments((prev) => ({
        ...prev,
        payStubs: { ...prev.payStubs, [key]: file, file: null, fileUrl: null },
      }));
      e.target.value = '';
    };
    input.click();
  };

  const removeNewFile = (type, index) => {
    setDocuments((prev) => {
      const list = [...(prev[type]?.newFiles || [])];
      list.splice(index, 1);
      return { ...prev, [type]: { ...prev[type], newFiles: list } };
    });
  };

  const handleReplace = (type) => {
    if (type === 'payStubs') {
      return;
    }
    if (type === 'landlordReference') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.jpg,.jpeg,.png';
      input.multiple = false;
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
          setDocuments((prev) => ({
            ...prev,
            landlordReference: { ...prev.landlordReference, file, fileName: file.name, fileUrl: null },
          }));
        }
      };
      input.click();
    } else {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = TENANT_DOC_ACCEPT_ATTR;
      input.multiple = true;
      input.onchange = (e) => handleMultiFileUpload(type, e);
      input.click();
    }
  };

  const handleView = (type) => {
    const doc = documents[type];
    const typeTitles = { payStubs: 'Id Verification', bankStatements: 'Bank Statements', landlordReference: 'Previous Landlord Reference' };
    const items = [];

    if (type === 'payStubs' && (doc.newFront || doc.newBack)) {
      const pushFile = (file, label) => {
        const blobUrl = URL.createObjectURL(file);
        items.push({
          url: blobUrl,
          isPdf: /\.pdf$/i.test(file.name) || file.type === 'application/pdf',
          _blobUrl: blobUrl,
          slideLabel: label,
        });
      };
      if (doc.newFront) pushFile(doc.newFront, 'Front');
      if (doc.newBack) pushFile(doc.newBack, 'Back');
    } else if (type === 'bankStatements' && doc.newFiles?.length) {
      doc.newFiles.forEach((file) => {
        const blobUrl = URL.createObjectURL(file);
        items.push({ url: blobUrl, isPdf: /\.pdf$/i.test(file.name) || file.type === 'application/pdf', _blobUrl: blobUrl });
      });
    } else if (doc.file) {
      const blobUrl = URL.createObjectURL(doc.file);
      const isPdf = doc.file.type === 'application/pdf' || /\.pdf$/i.test(doc.file.name);
      items.push({ url: blobUrl, isPdf, _blobUrl: blobUrl });
    } else {
      const urls = doc.fileUrls?.length ? doc.fileUrls : (doc.fileUrl ? [doc.fileUrl] : []);
      urls.forEach((url) => {
        const normalizedUrl = normalizeDocumentUrl(url);
        if (normalizedUrl) {
          items.push({ url: normalizedUrl, isPdf: /\.pdf$/i.test(normalizedUrl) });
        }
      });
    }

    if (!items.length) {
      console.log(`View ${type} document - no file or URL`);
      return;
    }

    setSelectedDocument({
      type: typeTitles[type] || type,
      items,
      currentIndex: 0,
    });
    setIsDocumentModalOpen(true);
  };

  const handleCloseDocumentModal = () => {
    if (selectedDocument?.items) {
      selectedDocument.items.forEach((item) => {
        if (item._blobUrl) URL.revokeObjectURL(item._blobUrl);
      });
    }
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  const goToSlide = (delta) => {
    if (!selectedDocument?.items?.length) return;
    const len = selectedDocument.items.length;
    const next = (selectedDocument.currentIndex + delta + len) % len;
    setSelectedDocument((prev) => ({ ...prev, currentIndex: next }));
  };

  const handleDownloadDocument = () => {
    const items = selectedDocument?.items;
    const idx = selectedDocument?.currentIndex ?? 0;
    const item = items?.[idx];
    if (!item?.url) return;
    const ext = item.isPdf ? 'pdf' : 'jpg';
    const filename = `${(selectedDocument.type || 'document').replace(/\s+/g, '_')}_${idx + 1}.${ext}`;
    const link = document.createElement('a');
    link.href = item.url;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLandlordForm = () => {
    setLandlordModalViewOnly(false);
    setLandlordModalInitialData(null);
    setIsModalOpen(true);
  };

  const handleLandlordViewForm = () => {
    const doc = documents.landlordReference;
    const data = doc?.landlordData || (doc?.file && typeof doc.file === 'object' ? { name: doc.file.name || '', phoneNumber: doc.file.phoneNumber || '', email: doc.file.email || '' } : null);
    setLandlordModalInitialData(data || null);
    setLandlordModalViewOnly(false);
    setIsModalOpen(true);
  };

  const handleLandlordSave = (data) => {
    setDocuments((prev) => ({
      ...prev,
      landlordReference: { ...prev.landlordReference, file: data, fileName: 'Previous Landlord Reference' },
    }));
    setIsModalOpen(false);
  };

  const hasLandlordFormData = () => {
    const doc = documents.landlordReference;
    if (doc?.landlordData && (doc.landlordData.name || doc.landlordData.phoneNumber || doc.landlordData.email)) return true;
    if (doc?.file && typeof doc.file === 'object' && (doc.file.name || doc.file.phoneNumber || doc.file.email)) return true;
    return false;
  };

  const handleSubmit = async () => {
    const ps = documents.payStubs;
    const payStubNew = [];
    if (ps?.newFront) payStubNew.push({ file: ps.newFront, side: 'front_side' });
    if (ps?.newBack) payStubNew.push({ file: ps.newBack, side: 'back_side' });
    const bankNew = documents.bankStatements?.newFiles || [];
    const hasNewPayStub = payStubNew.length > 0;
    const hasNewBankStatement = bankNew.length > 0;
    const landlordRef = documents.landlordReference?.file;
    const hasLandlordRefUpdate = landlordRef && typeof landlordRef === 'object' && (landlordRef.name || landlordRef.phoneNumber || landlordRef.email);

    if (!hasNewPayStub && !hasNewBankStatement && !hasLandlordRefUpdate) {
      toast.info(requireMinimumDocuments
        ? 'Please upload ID verification and bank statements before continuing.'
        : 'No changes to submit.');
      return;
    }

    if (requireMinimumDocuments) {
      const bs = documents.bankStatements;
      const hasExistingId = (ps?.fileUrls?.length > 0) || (ps?.fileUrl && String(ps.fileUrl).trim());
      const hasExistingBank = (bs?.fileUrls?.length > 0) || (bs?.fileUrl && String(bs.fileUrl).trim());
      const willHaveId = hasExistingId || payStubNew.length >= 2;
      const willHaveBank = hasExistingBank || bankNew.length > 0;

      if (payStubNew.length > 0 && payStubNew.length < 2 && !hasExistingId) {
        toast.error('Please upload both front and back of your ID.');
        return;
      }
      if (!willHaveId || !willHaveBank) {
        toast.error('Please upload ID verification (front and back) and at least one bank statement.');
        return;
      }
    }

    if (hasNewPayStub && !selectedIdDocumentType) {
      toast.error('Please select which type of ID document you are uploading.');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      let docIndex = 0;

      if (hasNewPayStub) {
        payStubNew.forEach(({ file, side }) => {
          formData.append(`documents[${docIndex}][file]`, file);
          formData.append(`documents[${docIndex}][type]`, selectedIdDocumentType);
          formData.append(`documents[${docIndex}][side]`, side);
          docIndex++;
        });
      }
      if (hasNewBankStatement) {
        bankNew.forEach((file, i) => {
          formData.append(`documents[${docIndex}][file]`, file);
          formData.append(`documents[${docIndex}][type]`, 'BANK_STATEMENT');
          formData.append(`documents[${docIndex}][side]`, i % 2 === 0 ? 'front_side' : 'back_side');
          docIndex++;
        });
      }
      if (hasLandlordRefUpdate) {
        if (landlordRef.name) formData.append('previousLandlordName', landlordRef.name);
        if (landlordRef.phoneNumber) formData.append('previousLandlordPhone', landlordRef.phoneNumber);
        if (landlordRef.email) formData.append('previousLandlordEmail', landlordRef.email);
      }

      const response = await postApi('/auth/upload-tenant-documents/v2', formData);

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Documents updated successfully.');
        if (hasNewPayStub) {
          setSelectedIdDocumentType('');
        }
        // Refetch documents so UI shows new URLs and clears local file state
        const res = await getApi('/auth/get-tenant-documents');
        const data = res?.data;
        if (data) {
          const payStubUrls = normalizeDocumentUrls(data.pay_stub_url);
          const bankStatementUrls = normalizeDocumentUrls(data.bank_statement);
          const payStubUrl = payStubUrls[0] || '';
          const bankStatementUrl = bankStatementUrls[0] || '';
          const hasLandlordReference = data.previous_landlord_name || data.previous_landlord_phone || data.previous_landlord_email;
          const landlordData = hasLandlordReference
            ? { name: data.previous_landlord_name || '', phoneNumber: data.previous_landlord_phone || '', email: data.previous_landlord_email || '' }
            : null;
          setDocuments({
            payStubs: { file: null, fileName: payStubUrl ? 'ID verification' : 'ID verification', fileUrl: payStubUrl || null, fileUrls: payStubUrls, newFront: null, newBack: null },
            bankStatements: { file: null, fileName: bankStatementUrl ? 'Bank statement pdf' : 'Bank statement pdf', fileUrl: bankStatementUrl || null, fileUrls: bankStatementUrls, newFiles: [] },
            landlordReference: { file: null, fileName: hasLandlordReference ? 'Previous landlord reference' : 'XYZ pdf', fileUrl: null, landlordData },
          });
          if (onUploadSuccess && tenantDocumentsFromApiData(data)) {
            onUploadSuccess();
          }
        } else if (onUploadSuccess && !requireMinimumDocuments) {
          onUploadSuccess();
        }
      } else {
        toast.error(response?.message || 'Failed to update documents.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update documents. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDisplayFileName = (file) => {
    if (!file?.name) return null;
    const maxLength = 30;
    return file.name.length > maxLength ? `${file.name.substring(0, maxLength - 3)}...` : file.name;
  };

  const DocumentCard = ({ type, title, description, fileName }) => {
    const doc = documents[type];
    const isBankMulti = type === 'bankStatements';
    const isPayId = type === 'payStubs';
    const newFiles = doc?.newFiles || [];
    const fileUrls = doc?.fileUrls || [];
    const hasNewFiles = newFiles.length > 0;
    const hasExistingUrls = fileUrls.length > 0;
    const hasNewIdPick = !!(doc?.newFront || doc?.newBack);
    const hasFile = isPayId
      ? !!(hasNewIdPick || hasExistingUrls || (doc?.fileUrl && typeof doc.fileUrl === 'string' && doc.fileUrl.trim() !== ''))
      : !!(hasNewFiles || hasExistingUrls || doc?.file || (doc?.fileUrl && typeof doc.fileUrl === 'string' && doc.fileUrl.trim() !== ''));
    const viewCount = isPayId
      ? hasNewIdPick
        ? (doc.newFront ? 1 : 0) + (doc.newBack ? 1 : 0)
        : fileUrls.length || (doc?.fileUrl && doc.fileUrl.trim() ? 1 : 0)
      : hasNewFiles
        ? newFiles.length
        : fileUrls.length || (doc?.file || (doc?.fileUrl && doc.fileUrl.trim()) ? 1 : 0);

    return (
      <div className="document-card">
        <div className="document-card-content">
          <div className="document-card-info">
            <h3 className="document-card-title">{title}</h3>
            <p className="document-card-subtitle">
              {description}
              {type === 'bankStatements' && MULTI_FILE_HINT_TEXT}
            </p>
            {type === 'payStubs' && (
              <fieldset className="document-card-id-type-fieldset">
                <legend className="document-card-id-type-legend">Document type</legend>
                <div className="document-card-id-type-radios" role="radiogroup" aria-label="ID document type">
                  {ID_VERIFICATION_TYPE_OPTIONS.map(({ value, label }) => (
                    <label key={value} className="document-card-id-type-option">
                      <input
                        type="radio"
                        name="profileIdDocumentType"
                        value={value}
                        checked={selectedIdDocumentType === value}
                        onChange={() => setSelectedIdDocumentType(value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            {type === 'payStubs' && (
              <div className="document-card-id-sides">
                <div className="document-card-id-side-row">
                  <span className="document-card-id-side-label">Front</span>
                  {doc.newFront && (
                    <span className="document-card-file-name document-card-id-side-filename">{getDisplayFileName(doc.newFront)}</span>
                  )}
                  <button
                    type="button"
                    className="document-card-button document-card-button-replace"
                    onClick={() => pickIdSide('front')}
                  >
                    {doc.newFront ? 'Change' : 'Upload'}
                  </button>
                </div>
                <div className="document-card-id-side-row">
                  <span className="document-card-id-side-label">Back</span>
                  {doc.newBack && (
                    <span className="document-card-file-name document-card-id-side-filename">{getDisplayFileName(doc.newBack)}</span>
                  )}
                  <button
                    type="button"
                    className="document-card-button document-card-button-replace"
                    onClick={() => pickIdSide('back')}
                  >
                    {doc.newBack ? 'Change' : 'Upload'}
                  </button>
                </div>
                {!hasNewIdPick && hasExistingUrls && (
                  <p className="document-card-file-name">{fileUrls.length} file(s) uploaded</p>
                )}
              </div>
            )}
            {hasFile && !isBankMulti && !isPayId && <p className="document-card-file-name">{fileName}</p>}
            {isBankMulti && hasNewFiles && (
              <ul className="document-card-file-list">
                {newFiles.map((file, idx) => (
                  <li key={`${type}-${idx}-${file.name}`} className="document-card-file-item">
                    <span className="document-card-file-name">{getDisplayFileName(file)}</span>
                    <button type="button" className="document-card-remove-file" onClick={() => removeNewFile(type, idx)} aria-label="Remove file">×</button>
                  </li>
                ))}
              </ul>
            )}
            {isBankMulti && !hasNewFiles && hasExistingUrls && (
              <p className="document-card-file-name">{fileUrls.length} file(s) uploaded</p>
            )}
          </div>
          <div className="document-card-actions">
            {type === 'landlordReference' && (
              hasLandlordFormData() ? (
                <button
                  className="document-card-button document-card-button-view"
                  onClick={handleLandlordViewForm}
                >
                  View Form
                </button>
              ) : (
                <button
                  className="document-card-button document-card-button-replace"
                  onClick={handleLandlordForm}
                  style={{ backgroundColor: 'white', color: '#1A5D3A', border: '1px solid #1A5D3A' }}
                >
                  Fill Form
                </button>
              )
            )}
            {hasFile && (type !== 'landlordReference' || !hasLandlordFormData()) && (
              <button
                className="document-card-button document-card-button-view"
                onClick={() => handleView(type)}
              >
                View ({viewCount})
              </button>
            )}
            {type !== 'payStubs' && !(type === 'landlordReference' && !hasFile && !hasLandlordFormData()) && (
              <button
                className="document-card-button document-card-button-replace"
                onClick={() => type === 'landlordReference' && !hasFile ? handleLandlordForm() : handleReplace(type)}
              >
                {hasFile ? 'Replace' : type === 'landlordReference' ? (hasLandlordFormData() ? 'Replace' : 'Fill Form') : 'Upload'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`settings-documents-card${embeddedInModal ? ' settings-documents-card--modal' : ''}`}>
      <div className="documents-header">
        <h2 className="documents-title">Upload Documents</h2>
        <p className="documents-subtitle">Required for income and identity verification</p>
      </div>

      <div className="documents-list">
        <DocumentCard
          type="payStubs"
          title="Id Verification"
          description="Front and back · Image or PDF"
          fileName={documents.payStubs.fileName}
        />
        <DocumentCard
          type="bankStatements"
          title="Bank Statements"
          description="Last 2 to 3 months"
          fileName={documents.bankStatements.fileName}
        />
        <DocumentCard
          type="landlordReference"
          title="Previous Landlord references"
          description="Contact info or letters"
          fileName={documents.landlordReference.fileName}
        />
      </div>

      <div className="documents-submit-wrapper">
        <button
          type="button"
          className="document-card-button document-card-button-submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <PreviousLandlordModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setLandlordModalInitialData(null);
        }}
        onSave={handleLandlordSave}
        initialData={landlordModalInitialData}
        viewOnly={landlordModalViewOnly}
      />

      {/* Document View Modal - same as landlord tenant details */}
      <Modal
        isOpen={isDocumentModalOpen}
        onRequestClose={handleCloseDocumentModal}
        contentLabel="Document View"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="document-view-modal"
        overlayClassName="document-view-modal-overlay"
      >
        <div className="document-view-modal-header">
          <h2 className="document-view-modal-title">
            {selectedDocument?.type || 'Document'}
            {selectedDocument?.items?.length > 1 && (
              <span className="document-view-modal-count">
                {' '}({ (selectedDocument.currentIndex ?? 0) + 1 } of { selectedDocument.items.length })
              </span>
            )}
          </h2>
          <div className="document-view-modal-header-actions">
            <button
              className="document-view-modal-download"
              onClick={handleDownloadDocument}
              title="Download"
            >
              <img
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/downloadBtn.svg") : "/assets/images/downloadBtn.svg"}
                alt="Download"
              />
            </button>
            <button
              className="document-view-modal-close"
              onClick={handleCloseDocumentModal}
              title="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="document-view-modal-content">
          {selectedDocument?.items?.length > 0 && (() => {
            const items = selectedDocument.items;
            const idx = selectedDocument.currentIndex ?? 0;
            const item = items[idx];
            if (!item) return null;
            return (
              <>
                <div className="document-view-carousel-wrapper">
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="document-view-carousel-prev"
                      onClick={() => goToSlide(-1)}
                      aria-label="Previous document"
                    >
                      ‹
                    </button>
                  )}
                  <div className="document-view-carousel-slide">
                    {item.isPdf ? (
                      <iframe
                        src={item.url}
                        title={`${selectedDocument.type} ${idx + 1}`}
                        className="document-view-modal-image"
                        style={{ width: '100%', minHeight: '70vh', border: 'none' }}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={`${selectedDocument.type} ${idx + 1}`}
                        className="document-view-modal-image"
                      />
                    )}
                  </div>
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="document-view-carousel-next"
                      onClick={() => goToSlide(1)}
                      aria-label="Next document"
                    >
                      ›
                    </button>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
};

export default Documents;
